import DialogContentText from "@material-ui/core/DialogContentText";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import * as React from "react";

import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IChip,
    Input,
    RelodeTheme,
    ScrollContainer,
    SelectInput,
} from "../";
import { ChipCollection } from "../chips";
import { useAsyncEffect, useDebounce } from "../hooks";
import { pascalCase } from "../utils";

// NOTE: We have tried to make this match ChipSelectionDialog as closely , as possible.
// TODO: seems like with a few more props, this could become MobileChipSelectionDialog...
const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {},
            paper: {
                overflow: "visible",
            },
            dialogTitle: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
            },
            dialogContent: {
                overflow: "visible",
                display: "flex",
                flexDirection: "column",
            },
            searchInput: {
                margin: theme.spacing(2, 0, 3, 0),
            },
            scrollContainer: {
                //height: "250px",
            },
            header: {
                display: "flex",
                justifyContent: "space-between",
            },
            paperAnchorTop: {
                height: "100%",
            },
            filterTitle: {
                margin: theme.spacing(2),
            },
            filterLink: {
                marginBottom: theme.spacing(6),
            },
            jobCategorySelection: {
                margin: 0,
                width: "100%",
            },
        }),
    { name: "MobileJobPositionDialog" }
);

interface MobileJobPositionProps {
    open: boolean;
    baseId: string;
    chipGroups?: IChip[];
    context: string;
    description?: string;
    closeOnAdd?: boolean;
    singleSelect?: boolean;
    onClose: () => void;
    onAdd: (selectedChips: IChip[]) => void;
    searchSuggestions: (
        searchText: string,
        groupsIds: number[],
        selectedChips?: IChip[]
    ) => any;
    pruneSelectedChips: (selectedChips?: IChip[]) => IChip[];
}

interface JobCategoryOption {
    value: number;
    label: string;
}

const getInitialState = (groups: IChip[]) => ({
    selectedChips: [],
    groupsChips: groups,
    matchableChips: [],
    currentSearchText: "",
});

// Only used in addJobPositionDialog.tsx
export const MobileJobPositionDialog = React.memo(
    (props: MobileJobPositionProps) => {
        const cleared = React.useRef(false);
        const {
            closeOnAdd,
            context,
            description,
            singleSelect,
            onAdd,
            onClose,
            open,
            chipGroups,
            pruneSelectedChips,
            searchSuggestions,
        } = props;

        const classes = useStyles({});
        const [selectedChips, setSelectedChips] = React.useState<IChip[]>([]);
        const [groupsChips, setGroupsChips] = React.useState<IChip[]>([]);
        const [matchableChips, setMatchableChips] = React.useState<IChip[]>([]);
        const [searchText, setSearchText] = React.useState("");
        const [scrollContentKey, setScrollContentKey] = React.useState(0);

        const getInitialChipGroups = (chips: IChip[]) => {
            const initialChipArray = [];
            chips.forEach(chip => {
                chip.isMatched = false;
                initialChipArray.push(chip);
            });
            return initialChipArray;
        };

        const clearState = React.useCallback(() => {
            cleared.current = true;
            const initialChipGroups = getInitialChipGroups(chipGroups);
            setInitialState(initialChipGroups || []);
        }, [chipGroups]);

        const setInitialState = (groups: IChip[]) => {
            const newState = getInitialState(groups);
            setSelectedChips(newState.selectedChips);
            setGroupsChips(newState.groupsChips);
            setMatchableChips(newState.matchableChips);
            setSearchText(newState.currentSearchText);
        };

        const handleClose = React.useCallback(() => {
            setScrollContentKey(0);
            if (!cleared.current) {
                clearState();
            }
            onClose();
        }, [clearState, onClose]);

        const handleAdd = React.useCallback(() => {
            onAdd(selectedChips);
            setScrollContentKey(o => o + 1);
            // perhaps let the caller just simply close on add?
            // Nothing really prevents the caller from doing so already.
            closeOnAdd && handleClose();
        }, [onAdd, closeOnAdd, selectedChips, handleClose]);

        React.useEffect(() => {
            if (singleSelect && selectedChips && selectedChips.length > 0) {
                onAdd(selectedChips);
                setScrollContentKey(o => o + 1);
                // perhaps let the caller just simply close on add?
                // Nothing really prevents the caller from doing so already.
                closeOnAdd && handleClose();
            }
        }, [
            closeOnAdd,
            handleClose,
            onAdd,
            scrollContentKey,
            selectedChips,
            singleSelect,
        ]);

        useAsyncEffect(async () => {
            if (open) {
                setGroupsChips(getInitialChipGroups(chipGroups) || []);
                await debouncedLoadSuggestions("", selectedChips, groupsChips);

                setScrollContentKey(o => o + 1);
            }
        }, [open]);

        const loadSuggestions = async (
            _searchText: string,
            _selectedChips: IChip[],
            _groupsChips: IChip[]
        ) => {
            const groupIds: number[] = [];
            _groupsChips.forEach(gc => {
                if (gc.isMatched || _groupsChips.every(c => !c.isMatched)) {
                    groupIds.push(gc.id);
                }
            });

            cleared.current = false;
            const mc = await searchSuggestions(
                _searchText,
                groupIds,
                _selectedChips
            );

            if (!mc) return;

            //remove selected chips if they are not in the suggestions list anymore
            setSelectedChips(pruneSelectedChips(_selectedChips));

            setMatchableChips(mc);
            setScrollContentKey(o => o + 1);
        };

        const debouncedLoadSuggestions = useDebounce(loadSuggestions, 500, []);

        // NOTE: this hook is needed because dropdown onchange doesn't allow async?
        useAsyncEffect(async () => {
            await debouncedLoadSuggestions(
                searchText,
                selectedChips,
                groupsChips
            );
        }, [searchText, groupsChips]);

        const handleTagTypeToggle = (e: never, newValue: JobCategoryOption) => {
            const chips = [...groupsChips];
            const matchedChip = chips.find(chip => chip.id === newValue.value);
            chips.forEach(c => (c.isMatched = false));
            if (matchedChip) {
                matchedChip.isMatched = true;
            }

            // reset selection and matched since we are changing the group
            setMatchableChips([]);
            setSelectedChips([]);
            setGroupsChips(chips);
            setScrollContentKey(o => o + 1);
        };

        const handleTagSelection = React.useCallback(
            (id: number) => {
                const matchableChipsCopy = { ...matchableChips };
                const selected = [...selectedChips];
                let matchedChip;

                Object.keys(matchableChipsCopy).forEach(type => {
                    const chips = [...matchableChipsCopy[type]];
                    const chip = chips.find(c => c.id === id);

                    if (chip) {
                        chip.isMatched = !chip.isMatched;
                        matchedChip = chip;

                        const index = selected.findIndex(t => t.id === id);
                        if (matchedChip.isMatched) {
                            if (index < 0) {
                                selected.push(chip);
                            }
                        } else {
                            selected.splice(index, 1);
                        }
                    }
                });
                setMatchableChips(matchableChipsCopy);
                setSelectedChips(selected);
            },
            [selectedChips, matchableChips]
        );

        const handleSearchTextChange = async (
            event: React.ChangeEvent<any>
        ) => {
            const text = event.target.value;
            setSearchText(text);
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            await debouncedLoadSuggestions(text, selectedChips, groupsChips);
        };

        const jobCategorySelections = React.useMemo(() => {
            if (!groupsChips) return;
            const jobCategoryOptions: JobCategoryOption[] = [
                { value: undefined, label: "all categories" },
            ];
            groupsChips.forEach(c =>
                jobCategoryOptions.push({ value: c.id, label: c.name })
            );

            return jobCategoryOptions;
        }, [groupsChips]);

        const chipDisplay = React.useMemo(
            () =>
                groupsChips.map(groupChip => (
                    <ChipCollection
                        id={`chipCollection-item-${pascalCase(
                            groupChip.name,
                            true
                        )}`}
                        key={groupChip.id}
                        description={
                            groupChip.isMatched ||
                            groupsChips.every(c => !c.isMatched)
                                ? groupChip.name
                                : undefined
                        }
                        chips={matchableChips[groupChip.id!]}
                        onChipClick={handleTagSelection}
                    />
                )),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [groupsChips, matchableChips]
        );

        return (
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle title={`add ${context}`} onClose={handleClose} />
                <DialogContent>
                    {description && (
                        <DialogContentText>{description}</DialogContentText>
                    )}
                    {!!chipGroups ? (
                        <SelectInput
                            mode="list"
                            className={classes.jobCategorySelection}
                            label="job category"
                            id="jobCategorySelection"
                            options={jobCategorySelections}
                            defaultValue={{ value: undefined, label: "all categories" }}
                            onChange={handleTagTypeToggle}
                            getOptionLabel={option => option.label}
                            getOptionSelected={(option, value) =>
                                option.value === value.value
                            }
                        />
                    ) : null}
                    <Input
                        fullWidth
                        autoFocus
                        className={classes.searchInput}
                        id="searchInput"
                        label={`type to filter ${context}`}
                        value={searchText}
                        onChange={handleSearchTextChange}
                    />
                    <ScrollContainer
                        className={classes.scrollContainer}
                        scrollContentKey={scrollContentKey.toString()}
                        showMoreIcon={false}
                    >
                        {chipDisplay}
                    </ScrollContainer>
                </DialogContent>
                {!singleSelect ? (
                    <DialogActions
                        submit={{
                            label: "add",
                            disabled: selectedChips.length === 0,
                            onSubmit: handleAdd,
                        }}
                    />
                ) : null}
            </Dialog>
        );
    }
);
